import React, { useContext, useState } from "react";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import VendorContext from "@contexts/VendorContext";
import styled from "styled-components";
import IConnection_Token from "@models/IConnection_Token";
import { Socket } from "socket.io-client";
import ILoadUserDTO from "@models/DTO/ILoadUserDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import UIService from "@services/UIService";
import * as _ from "lodash-es";
import OrderwerksGreyLogo from "@assets/images/orderwerks/orderwerks-logo-grey.svg";
import OrderwerksDarkLogo from "@assets/images/orderwerks/orderwerks-logo-dark.svg";
import { useNavigate } from "react-router-dom";
import TokenTypesEnum from "@models/TokenTypesEnum";
import UiContext from "@contexts/UiContext";
import UserTypesEnum from "@models/UserTypesEnum";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";

type Props = {
    socket: Socket;
    menuIconMode: boolean;
    hideMenuIconMode: boolean;
    toggleMenuIconMode: () => void;
    toggleAccountSwitcherOpen: (isOpen: boolean) => void;
};

export const HeaderAccountSwitcher = (props: Props) => {
    const history = useNavigate();

    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const uiContext = useContext(UiContext);

    const [viewTokens, setViewTokens] = useState(false);

    const toggleViewTokens = () => {
        if (userModel.userSwitchTokens?.length > 1) {
            setViewTokens(!viewTokens);
            props.toggleAccountSwitcherOpen(!viewTokens);
        }
    };

    const switchToken = (e: React.MouseEvent<any>, token: IConnection_Token) => {
        e.preventDefault();
        e.stopPropagation();

        history("/accounting_change/loading");

        const payload: ILoadUserDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            // supplierGuid: token.Supplier?.guid,
            switchTokenGuid: token.guid,
        };

        props.socket.emit(EndpointsEnum.AppLoadUser, payload);

        setViewTokens(false);

        uiContext.toggleRightSide(false);
    };

    const isHeaderLight = () => {
        const headerElem = document.getElementById("HeaderStandard");

        if (headerElem) {
            const bgColor = window.getComputedStyle(headerElem, null).getPropertyValue("background-color");

            const colorMatch = bgColor.match(/(\d+),\s*(\d+),\s*(\d+)/);

            // console.log("colorMatch", colorMatch);

            if (colorMatch) {
                const r = Number(colorMatch[1]);
                const g = Number(colorMatch[2]);
                const b = Number(colorMatch[3]);

                const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

                // console.log("isHeaderLight", hsp);
                if (hsp > 127.5) {
                    return true;
                } else {
                    return false;
                }
            }
        }
    };

    const getTokenUserType = (token: IConnection_Token, isCurrent: boolean) => {
        const textColor = isCurrent ? "text-dark" : "text-muted";

        if (token.user_type_id === UserTypesEnum.ADMIN) {
            return (
                <span className={`${textColor} text-small`}>
                    (<OwTranslate id={"admin"} defaultMessage={"Admin"} />)
                </span>
            );
        } else if (token.user_type_id === UserTypesEnum.SALES) {
            return (
                <span className={`${textColor} text-small`}>
                    (<OwTranslate id={"sales"} defaultMessage={"Sales"} />)
                </span>
            );
        }
    };

    let supplierLogo = false;
    let supplierLogoUrl = "";

    if (
        supplierModel &&
        supplierModel.has_white_label &&
        (supplierModel.white_label_image_attachment_id || supplierModel.white_label_image_light_attachment_id)
    ) {
        supplierLogo = true;

        const { themeType } = UIService.getThemeType(supplierModel, userModel);

        if (themeType === "LIGHT") {
            supplierLogoUrl = _.get(supplierModel, "White_Label_Light_Image.signedUrl", "");
        } else {
            supplierLogoUrl = _.get(supplierModel, "White_Label_Image.signedUrl", "");
        }
    }

    let OrderwerksLogo = OrderwerksGreyLogo;

    if (isHeaderLight()) {
        OrderwerksLogo = OrderwerksDarkLogo;
    }

    return (
        <AccountSwitcherWrapper className={"HeaderAccountSwitcher d-flex justify-content-between"}>
            {userModel ? (
                <AccountSwitcher pointer={userModel.userSwitchTokens?.length > 1} onClick={toggleViewTokens}>
                    {!supplierLogo ? (
                        <DefaultLogo alt="Orderwerks B2B Sales Order Entry Logo" src={OrderwerksLogo} height={35} />
                    ) : (
                        <img alt={"Orderwerks B2B Sales Order Entry"} src={supplierLogoUrl} height={35} />
                    )}
                    {viewTokens ? (
                        <AccountSwitcherTokens className={"HeaderAccountSwitcher__tokens"}>
                            {userModel.userSwitchTokens?.map((token, index) => {
                                let isCurrent = false;

                                if (token.token_type_id === TokenTypesEnum.SUPPLIER_USER) {
                                    // currently on the api we set the supplier_id to signify the master supplier vendor model
                                    if (
                                        supplierModel.id === token.supplier_id &&
                                        vendorModel.supplier_id === token.supplier_id &&
                                        userModel?.supplierUserToken?.id === token.id
                                    ) {
                                        isCurrent = true;
                                    }
                                } else if (token.token_type_id === TokenTypesEnum.VENDOR_USER) {
                                    if (vendorModel.id === token.vendor_id) {
                                        isCurrent = true;
                                    }
                                }

                                return (
                                    <AccountTokenRow
                                        key={index}
                                        className={"HeaderAccountSwitcher__token"}
                                        onClick={(e) => {
                                            switchToken(e, token);
                                        }}>
                                        <div
                                            className={
                                                "HeaderAccountSwitcher__token__name  " + (isCurrent ? "bg-secondary rounded-5" : "")
                                            }>
                                            {isCurrent ? <i className={"fas fa-check-circle me-2"} /> : ""}
                                            {token.Supplier?.name || token.Vendor?.name} {getTokenUserType(token, isCurrent)}
                                        </div>
                                        {/*<span className={"HeaderAccountSwitcher__token__switch"}>Switch</span>*/}
                                    </AccountTokenRow>
                                );
                            })}
                        </AccountSwitcherTokens>
                    ) : null}
                    {userModel.userSwitchTokens?.length > 1 ? (
                        <i className={`ms-3 fas ${viewTokens ? "fa-chevron-up" : "fa-chevron-down"}`} />
                    ) : null}
                </AccountSwitcher>
            ) : (
                <DefaultLogo alt="Orderwerks B2B Sales Order Entry Logo" src={OrderwerksLogo} height={35} />
            )}
        </AccountSwitcherWrapper>
    );
};

const AccountSwitcherWrapper = styled.div`
    min-width: 15rem;
`;

const AccountSwitcher = styled.div<{ pointer: boolean }>`
    position: relative;
    width: 100%;
    height: 42px;
    line-height: 42px;
    cursor: ${(p) => (p.pointer ? "pointer" : "default")};
    display: flex;
    z-index: 10;
    text-align: center;
    justify-content: space-between;
    align-items: center;
`;

const AccountSwitcherTokens = styled.div`
    position: absolute;
    top: 42px;
    left: 0;
    width: 100%;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.5);
    z-index: 100;
    background-color: var(--main-bg-color);
`;

const AccountTokenRow = styled.div`
    padding: 0.5rem;
    &:hover {
        background-color: rgba(0, 0, 0, 0.2);
    }
`;

const DefaultLogo = styled.img`
    margin-top: 0.3rem;
`;
